import { FormationSchema, FormationType, TeamSize } from '@Global/types/formations';
import { PlayerPosition } from '@Apollo/types';

/**
 * Флексибльная схема для команды 7+1
 * Показывает ВСЕ возможные позиции, валидация происходит динамически
 */
export const FLEXIBLE_EXTENDED_FORMATION: FormationSchema = {
  type: FormationType.EXTENDED_1_2_2_2_1, // Базовый тип для совместимости
  teamSize: TeamSize.EXTENDED,
  positions: {
    [PlayerPosition.Goalkeeper]: 1,
    [PlayerPosition.Back]: 3, // Максимально возможное количество
    [PlayerPosition.Midfield]: 3, // Максимально возможное количество
    [PlayerPosition.Forward]: 2, // Максимально возможное количество
    [PlayerPosition.Reserve]: 1,
  },
  layout: [
    // Вратарь (обязательный)
    {
      position: PlayerPosition.Goalkeeper,
      index: 0,
      key: 'goalkeeper-0',
      cssClass: 'tw-absolute tw-w-[60px] tw-aspect-[2/3] tw-top-[-80px] tw-left-[134px]',
    },

    // Защитники (минимум 2, максимум 3)
    {
      position: PlayerPosition.Back,
      index: 0,
      key: 'back-0',
      cssClass: 'tw-absolute tw-w-[60px] tw-aspect-[2/3] tw-top-[16px] tw-left-[37px]',
    },
    {
      position: PlayerPosition.Back,
      index: 1,
      key: 'back-1',
      cssClass: 'tw-absolute tw-w-[60px] tw-aspect-[2/3] tw-top-[16px] tw-left-[134px]',
    },
    {
      position: PlayerPosition.Back,
      index: 2,
      key: 'back-2',
      cssClass: 'tw-absolute tw-w-[60px] tw-aspect-[2/3] tw-top-[16px] tw-left-[231px]',
    },

    // Полузащитники (минимум 2, максимум 3)
    {
      position: PlayerPosition.Midfield,
      index: 0,
      key: 'midfield-0',
      cssClass: 'tw-absolute tw-w-[60px] tw-aspect-[2/3] tw-top-[125px] tw-left-[37px]',
    },
    {
      position: PlayerPosition.Midfield,
      index: 1,
      key: 'midfield-1',
      cssClass: 'tw-absolute tw-w-[60px] tw-aspect-[2/3] tw-top-[125px] tw-left-[134px]',
    },
    {
      position: PlayerPosition.Midfield,
      index: 2,
      key: 'midfield-2',
      cssClass: 'tw-absolute tw-w-[60px] tw-aspect-[2/3] tw-top-[125px] tw-left-[231px]',
    },

    // Нападающие (минимум 1, максимум 2)
    {
      position: PlayerPosition.Forward,
      index: 0,
      key: 'forward-0',
      cssClass: 'tw-absolute tw-w-[60px] tw-aspect-[2/3] tw-top-[240px] tw-left-[85px]',
    },
    {
      position: PlayerPosition.Forward,
      index: 1,
      key: 'forward-1',
      cssClass: 'tw-absolute tw-w-[60px] tw-aspect-[2/3] tw-top-[240px] tw-left-[185px]',
    },

    // Резерв
    {
      position: PlayerPosition.Reserve,
      index: 0,
      key: 'reserve-0',
      cssClass: 'tw-absolute tw-w-[48px] tw-aspect-[2/3] tw-top-[-75px] tw-left-[265px]',
    },
  ],
};

/**
 * Минимальные требования для валидной команды 7+1
 */
export const EXTENDED_TEAM_REQUIREMENTS = {
  [PlayerPosition.Goalkeeper]: { min: 1, max: 1 },
  [PlayerPosition.Back]: { min: 2, max: 3 },
  [PlayerPosition.Midfield]: { min: 2, max: 3 },
  [PlayerPosition.Forward]: { min: 1, max: 2 },
  [PlayerPosition.Reserve]: { min: 1, max: 1 },
  totalFieldPlayers: 7, // Общее количество полевых игроков (без вратаря и резерва)
};

/**
 * Стандартная формация для команды 5+1 с поддержкой разных лейаутов
 */
export const getStandardFormation = (hasReservePosition: boolean): FormationSchema => {
  return {
    type: FormationType.STANDARD_1_1_1_1_1,
    teamSize: TeamSize.STANDARD,
    positions: {
      [PlayerPosition.Goalkeeper]: 1,
      [PlayerPosition.Back]: 1,
      [PlayerPosition.Midfield]: 1,
      [PlayerPosition.Forward]: 1,
      [PlayerPosition.Reserve]: 1,
    },
    layout: [
      // Вратарь
      {
        position: PlayerPosition.Goalkeeper,
        index: 0,
        key: 'goalkeeper-0',
        cssClass: hasReservePosition
          ? 'tw-absolute tw-w-[90px] tw-aspect-[2/3] tw-top-[-84px] tw-left-[119px]' // С резервом - центр
          : 'tw-absolute tw-w-[90px] tw-aspect-[2/3] tw-top-[-80px] tw-left-[67px]', // С флагами - слева
      },

      // Защитник
      {
        position: PlayerPosition.Back,
        index: 0,
        key: 'back-0',
        cssClass: 'tw-absolute tw-w-[90px] tw-aspect-[2/3] tw-top-[60px] tw-left-0',
      },

      // Полузащитник
      {
        position: PlayerPosition.Midfield,
        index: 0,
        key: 'midfield-0',
        cssClass: 'tw-absolute tw-w-[90px] tw-aspect-[2/3] tw-top-[60px] tw-left-[230px]',
      },

      // Нападающий
      {
        position: PlayerPosition.Forward,
        index: 0,
        key: 'forward-0',
        cssClass: 'tw-absolute tw-w-[90px] tw-aspect-[2/3] tw-top-[165px] tw-left-[118px]',
      },

      // Резерв
      {
        position: PlayerPosition.Reserve,
        index: 0,
        key: 'reserve-0',
        cssClass: hasReservePosition
          ? 'tw-absolute tw-w-[60px] tw-aspect-[2/3] tw-top-[-75px] tw-left-[240px]' // С резервом - маленький в углу
          : 'tw-absolute tw-w-[90px] tw-aspect-[2/3] tw-top-[-80px] tw-left-[170px]', // С флагами - справа
      },
    ],
  };
};

/**
 * Получить правильную формацию в зависимости от размера команды
 */
export const getFlexibleFormation = (teamSize: TeamSize | number, hasReservePosition?: boolean): FormationSchema => {
  if (teamSize === TeamSize.EXTENDED) {
    return FLEXIBLE_EXTENDED_FORMATION;
  }

  // Для стандартной команды возвращаем формацию с учетом hasReservePosition
  return getStandardFormation(hasReservePosition ?? true);
};
